import React, { useMemo } from 'react'
import { Source, Layer } from 'react-map-gl/mapbox'

const NODE_COLORS = {
  supplier: '#10b981',
  factory: '#f59e0b',
  distributor: '#3b82f6',
  retailer: '#8b5cf6',
}

const NODE_COLORS_DARK = {
  supplier: '#34d399',
  factory: '#fbbf24',
  distributor: '#60a5fa',
  retailer: '#a78bfa',
}

// Quadratic bezier with a gentle perpendicular arc between two map coordinates
export function getBezierCoordinates(start, end, segments = 32) {
  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  const len = Math.sqrt(dx * dx + dy * dy)
  if (len < 0.001) return [start, end]

  const mx = (start[0] + end[0]) / 2
  const my = (start[1] + end[1]) / 2
  // Perpendicular offset — capped so long-distance lines don't arc wildly
  const offset = Math.min(len * 0.18, 2.5)
  const cx = mx - (dy / len) * offset
  const cy = my + (dx / len) * offset

  const pts = []
  for (let i = 0; i <= segments; i++) {
    const t = i / segments
    pts.push([
      (1 - t) * (1 - t) * start[0] + 2 * (1 - t) * t * cx + t * t * end[0],
      (1 - t) * (1 - t) * start[1] + 2 * (1 - t) * t * cy + t * t * end[1],
    ])
  }
  return pts
}

function MapConnectionLayer({ nodes, edges, themeMode, simulationResults, showConnections = true }) {
  const isDark = themeMode === 'dark'
  const colors = isDark ? NODE_COLORS_DARK : NODE_COLORS

  // Compute per-edge flow intensity from simulation results
  const flowData = useMemo(() => {
    if (!simulationResults?.individual_stats) return null

    const stats = simulationResults.individual_stats
    const edgeFlows = edges.map(edge => {
      const sourceStats = stats[edge.source]?.stats
      const targetStats = stats[edge.target]?.stats

      let flow = 0
      if (sourceStats?.demand_fulfilled) {
        flow = sourceStats.demand_fulfilled[1] || 0
      } else if (targetStats?.demand_received) {
        flow = targetStats.demand_received[1] || 0
      }

      return { edgeId: edge.id, flow }
    })

    const flows = edgeFlows.map(e => e.flow)
    const maxFlow = Math.max(...flows, 1)
    const minFlow = Math.min(...flows)

    return edgeFlows.reduce((acc, { edgeId, flow }) => {
      const normalized = maxFlow === minFlow ? 0.5 : (flow - minFlow) / (maxFlow - minFlow)
      acc[edgeId] = {
        flow,
        normalized,
        width: 2 + normalized * 8,
        glowWidth: 6 + normalized * 12,
        opacity: 0.5 + normalized * 0.5,
      }
      return acc
    }, {})
  }, [simulationResults, edges])

  const geojson = useMemo(() => {
    const features = edges
      .map(edge => {
        const source = nodes.find(n => n.id === edge.source)
        const target = nodes.find(n => n.id === edge.target)
        if (!source?.data.lat || !target?.data.lat) return null

        const sourceType = source.data.nodeType || 'supplier'
        const start = [source.data.lng, source.data.lat]
        const end = [target.data.lng, target.data.lat]

        return {
          type: 'Feature',
          properties: {
            id: edge.id,
            color: colors[sourceType] || colors.supplier,
            label: edge.label || '',
            edgeCost: edge.data?.cost ?? '',
            edgeLeadTime: edge.data?.lead_time ?? '',
            lineWidth: flowData?.[edge.id]?.width ?? 2.5,
            glowWidth: flowData?.[edge.id]?.glowWidth ?? 6,
            lineOpacity: flowData?.[edge.id]?.opacity ?? 0.85,
          },
          geometry: {
            type: 'LineString',
            coordinates: getBezierCoordinates(start, end),
          },
        }
      })
      .filter(Boolean)

    return { type: 'FeatureCollection', features }
  }, [nodes, edges, colors, flowData])

  const lineColor = isDark ? '#818cf8' : '#4f46e5'

  const emptyGeojson = { type: 'FeatureCollection', features: [] }

  return (
    <Source id="connection-lines" type="geojson" data={showConnections ? geojson : emptyGeojson}>
      {/* Outer glow line */}
      <Layer
        id="connection-lines-glow"
        type="line"
        paint={{
          'line-color': lineColor,
          'line-width': ['get', 'glowWidth'],
          'line-opacity': 0.15,
          'line-blur': 4,
        }}
        layout={{
          'line-cap': 'round',
          'line-join': 'round',
        }}
      />
      {/* Main connection line */}
      <Layer
        id="connection-lines-main"
        type="line"
        paint={{
          'line-color': ['get', 'color'],
          'line-width': ['get', 'lineWidth'],
          'line-opacity': ['get', 'lineOpacity'],
        }}
        layout={{
          'line-cap': 'round',
          'line-join': 'round',
        }}
      />
      {/* Dashed directional overlay */}
      <Layer
        id="connection-lines-dash"
        type="line"
        paint={{
          'line-color': isDark ? '#e2e8f0' : '#1e293b',
          'line-width': 1,
          'line-opacity': 0.3,
          'line-dasharray': [2, 4],
        }}
        layout={{
          'line-cap': 'round',
          'line-join': 'round',
        }}
      />
    </Source>
  )
}

export default React.memo(MapConnectionLayer)
